import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router';
import { getUroSDK, getUroSocket, listUnifiedJobs } from '../../../services/URO';
import imageUploadActions from '../../../actions/imageUpload';
import jobActions from '../../../actions/jobs';
import secureAxios from '../../../actions/axios/secureAxios';
import ZenControlPanel from './components/ZenControlPanel';
import SettingsModal from './components/SettingsModal';
import GenerationCard, { getOutputs, getPrompt, getInputImages } from '../../../components/GenerationCard';
import ModelSelectorModal from './components/ModelSelectorModal';
import ModelSelectorSheet from './components/ModelSelectorSheet';
import ZenGenerationModal from './components/ZenGenerationModal';
import ZenGalleryView from './components/ZenGalleryView';
import ImageViewerModal from './components/ImageViewerModal';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';
import { classifyMediaParam, isMediaName, formatParamLabel } from '../createScreen/utils/parameterUtils';
import { GridIcon, ListIcon, GalleryViewIcon, CaretIcon, CloseIcon } from '../../../components/app-shell/icons';
import './zenScreen.css';

// Parse allowed_values from various formats (string JSON, comma-separated, array, scalar)
function parseAllowedValues(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [p]; }
    catch { return raw.split(',').map(v => v.trim()); }
  }
  return [raw];
}

// textInputs holds strings AND numbers/booleans (model defaults land there), so
// anything that treats a value as text has to check the type first — calling
// .trim() on a numeric default is what used to blow up onGenerate.
function isFilled(v) {
  if (typeof v === 'string') return v.trim().length > 0;
  return v !== undefined && v !== null && v !== '';
}

// Param names a provider uses for its own internal batching. The Batch control
// owns run count, so these are stripped before submit to stop the two
// multiplying each other (batch 4 × num_images 4 = 16 charged outputs).
const NATIVE_BATCH_PARAMS = ['num_images', 'num_outputs', 'batch_size', 'num_samples', 'n'];

const VIEW_MODES = [
  { id: 'grid',    label: 'Grid',    Icon: GridIcon,        hint: 'Aligned tiles, newest first, left to right' },
  { id: 'gallery', label: 'Gallery', Icon: GalleryViewIcon, hint: 'One big preview with a filmstrip below (← → to move)' },
  { id: 'list',    label: 'List',    Icon: ListIcon,        hint: 'One generation per row with its prompt' },
];

const GRID_SIZES = { S: 128, M: 176, L: 260 };

// The control panel used to be a hard 400px at every width. With the 240px app
// sidebar that left 128px of results at 768 and two giant tiles at 1280 — the
// "not responsive" complaint. Below COMPACT_MAX the panel stops being a column
// and becomes a sheet, so results always get the full width.
// Only show the panel as a column once there is room for ~3 result columns
// beside it; below that the sheet wins and results get the full width.
const COMPACT_MAX = 1180;
function panelWidthFor(w) {
  if (w >= 1700) return 400;
  if (w >= 1440) return 372;
  if (w >= 1280) return 340;
  return 312;
}

// One-click starters so the empty screen isn't a blank prompt box.
const STARTER_PROMPTS = [
  { label: 'Product shot',  text: 'Studio product photograph of a matte ceramic coffee cup on a polished concrete surface, soft window light from the left, shallow depth of field, subtle reflections, minimal neutral background' },
  { label: 'Portrait',      text: 'Editorial portrait of a person in a linen shirt against a warm terracotta wall, golden hour side light, 85mm lens, shallow depth of field, natural skin texture' },
  { label: 'Logo / mark',   text: 'Minimal geometric logo mark of a mountain and a rising sun, flat vector, two colours, thick even strokes, centred on a plain background, no text' },
  { label: 'Landscape',     text: 'Wide cinematic landscape of mist rolling through pine-covered mountains at dawn, layered depth, cool blue shadows and warm light on the peaks, ultra detailed' },
  { label: 'Illustration',  text: 'Flat vector illustration of a cosy reading nook with a big window, muted pastel palette, soft grain texture, gentle geometric shapes' },
  { label: 'Ad banner',     text: 'Bold advertising key visual with a floating pair of running shoes, vivid gradient background, dynamic motion streaks, strong rim lighting, generous empty space for a headline' },
];

const readLS = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : v; } catch { return fallback; }
};

export default function ZenScreen() {
  const { balance, clientName, clientEmail } = useClientData();
  const { modelId, workflowId } = useParams(); // /zen/model/:modelId or /zen/workflow/:workflowId
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const socketRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Check for reuse job state from gallery navigation
  const reuseJobState = location.state?.reuseJob ? location.state : null;

  // Viewport tracking. `isMobile` still gates the phone-only bits (bottom sheet
  // for the model selector); `isCompact` gates the whole side-panel-vs-sheet
  // layout decision and kicks in a breakpoint earlier, because a 240px sidebar
  // plus a side panel does not fit on a tablet.
  const [viewportW, setViewportW] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = viewportW < 768;
  const isCompact = viewportW < COMPACT_MAX;
  const panelWidth = panelWidthFor(viewportW);

  // Core state
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);
  const [textInputs, setTextInputs] = useState({}); // { paramName: value }
  const [uploadedImages, setUploadedImages] = useState(null); // { paramName: url/[urls] }
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Generations in current session (conversation-like)
  const [generations, setGenerations] = useState([]);

  // UI state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [detailViewJob, setDetailViewJob] = useState(null);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'image' | 'workflow'
  const [modelFilter, setModelFilter] = useState(null); // model_id or null
  const [controlsOpen, setControlsOpen] = useState(false); // mobile sheet

  // View preferences — sticky across sessions so the screen opens how you left it
  const [viewMode, setViewMode] = useState(() => {
    const v = readLS('zen_view_mode', 'grid');
    return VIEW_MODES.some(m => m.id === v) ? v : 'grid';
  });
  const [gridSize, setGridSize] = useState(() => {
    const v = readLS('zen_grid_size', 'M');
    return GRID_SIZES[v] ? v : 'M';
  });
  const [sortOrder, setSortOrder] = useState(() => (readLS('zen_sort', 'newest') === 'oldest' ? 'oldest' : 'newest'));
  const [batchCount, setBatchCount] = useState(() => {
    const n = Number(readLS('zen_batch_count', '1'));
    return n >= 1 && n <= 4 ? n : 1;
  });
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Likes — optimistic set of job ids
  const [likedIds, setLikedIds] = useState(() => new Set());
  const [likePendingIds, setLikePendingIds] = useState(() => new Set());

  // Before/after viewer
  const [compareState, setCompareState] = useState(null); // { images, inputImages, initialIndex }

  useEffect(() => { try { localStorage.setItem('zen_view_mode', viewMode); } catch { /* quota */ } }, [viewMode]);
  useEffect(() => { try { localStorage.setItem('zen_grid_size', gridSize); } catch { /* quota */ } }, [gridSize]);
  useEffect(() => { try { localStorage.setItem('zen_sort', sortOrder); } catch { /* quota */ } }, [sortOrder]);
  useEffect(() => { try { localStorage.setItem('zen_batch_count', String(batchCount)); } catch { /* quota */ } }, [batchCount]);

  // Settings (persisted to localStorage)
  const [settings, setSettings] = useState(() => {
    const defaults = {
      steps: 20,
      cfg_scale: 7,
      sampler: 'euler',
      seed: -1,
      negative_prompt: '',
      num_images: 1,
      ratio: null,
      quality: null,
    };
    try {
      const saved = localStorage.getItem('zen_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migrate legacy width/height settings → drop them, use ratio/quality
        delete parsed.width;
        delete parsed.height;
        return { ...defaults, ...parsed };
      }
      return defaults;
    } catch {
      return defaults;
    }
  });

  // Toast notification helper — one timer, so a new toast can't be cleared early
  // by the previous toast's pending timeout.
  const notificationTimerRef = useRef(null);
  const showNotification = useCallback((msg, type = 'info') => {
    setNotification({ msg, type });
    if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = setTimeout(() => setNotification(null), 3200);
  }, []);
  useEffect(() => () => { if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current); }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('zen_settings', JSON.stringify(settings));
  }, [settings]);

  // Update generation by job ID
  const updateGenerationByJobId = (jobId, updates) => {
    if (!jobId) return; // a null id would match every not-yet-submitted row
    setGenerations(prev => prev.map(gen => (gen.jobId === jobId ? { ...gen, ...updates } : gen)));
  };

  // Fetch models on mount and setup socket
  useEffect(() => {
    fetchModels();

    // Setup socket listeners (same as PlaygroundScreen)
    const socket = getUroSocket();
    socketRef.current = socket;

    // Helper to get job ID from socket data (works for both models and workflows)
    const getJobId = (data) => data.image_job_id || data.workflow_job_id;

    const onCreated = (data) => {
      const jobId = getJobId(data);
      updateGenerationByJobId(jobId, { status: 'pending', jobId });
    };

    const onProcessing = (data) => {
      updateGenerationByJobId(getJobId(data), { status: 'processing' });
    };

    const onComplete = (data) => {
      // Helper to detect content type from URL or explicit type
      const detectContentType = (item) => {
        if (item.content_type) return item.content_type;
        const url = item.external_url || item.url || '';
        if (url.match(/\.(mp4|webm|mov|avi|mkv)/i)) return 'video';
        if (url.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)/i)) return 'image';
        if (typeof item.content === 'string' && !url) return 'text';
        return 'image'; // default fallback
      };

      // Extract ALL results with their content types
      let outputs = [];

      // Model jobs: data.results is array directly
      if (data.results && Array.isArray(data.results)) {
        outputs = data.results.map(r => ({
          url: r.external_url || r.url || null,
          content: r.content || null,
          type: detectContentType(r)
        })).filter(o => o.url || o.content);
      }
      // Workflow jobs: data.results.results is nested array
      else if (data.results?.results && Array.isArray(data.results.results)) {
        outputs = data.results.results.map(r => ({
          url: r.external_url || r.url || null,
          content: r.content || null,
          type: detectContentType(r)
        })).filter(o => o.url || o.content);
      }

      // For backwards compatibility, also extract just image URLs
      const images = outputs
        .filter(o => o.type === 'image')
        .map(o => o.url)
        .filter(Boolean);

      updateGenerationByJobId(getJobId(data), {
        status: 'completed',
        images, // Keep for backwards compatibility
        outputs, // New: all outputs with types
        cost: data.cost_to_client,
        completedAt: new Date()
      });
    };

    const onError = (data) => {
      updateGenerationByJobId(getJobId(data), {
        status: 'failed',
        error: data.error || 'Generation failed'
      });
    };

    // Listen for model job events
    socket.on('job:created', onCreated);
    socket.on('job:processing', onProcessing);
    socket.on('job:complete', onComplete);
    socket.on('job:error', onError);

    // Listen for workflow job events (different event names!)
    socket.on('workflow:created', onCreated);
    socket.on('workflow:processing', onProcessing);
    socket.on('workflow:complete', onComplete);
    socket.on('workflow:failed', onError); // Note: 'failed' not 'error'
    socket.on('workflow:error', onError);

    return () => {
      if (socket && socket.off) {
        socket.off('job:created', onCreated);
        socket.off('job:processing', onProcessing);
        socket.off('job:complete', onComplete);
        socket.off('job:error', onError);
        socket.off('workflow:created', onCreated);
        socket.off('workflow:processing', onProcessing);
        socket.off('workflow:complete', onComplete);
        socket.off('workflow:failed', onError);
        socket.off('workflow:error', onError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModels = async () => {
    try {
      setModelsLoading(true);

      // Fetch both zen_enabled models and workflows in parallel
      const [modelsRes, workflowsRes] = await Promise.all([
        getUroSDK().listModels({ limit: 100, zen_enabled: 1 }),
        getUroSDK().listWorkflows({ limit: 100, zen_enabled: 1 })
      ]);

      // Parse models
      let modelItems = [];
      const modelData = modelsRes.data;
      if (Array.isArray(modelData)) modelItems = modelData;
      else if (Array.isArray(modelData?.items)) modelItems = modelData.items;
      else if (Array.isArray(modelData?.models)) modelItems = modelData.models;
      else if (Array.isArray(modelData?.data)) modelItems = modelData.data;

      // Parse workflows and normalize to model-like structure
      let workflowItems = [];
      const workflowData = workflowsRes.data;
      if (Array.isArray(workflowData)) workflowItems = workflowData;
      else if (Array.isArray(workflowData?.items)) workflowItems = workflowData.items;
      else if (Array.isArray(workflowData?.workflows)) workflowItems = workflowData.workflows;
      else if (Array.isArray(workflowData?.data)) workflowItems = workflowData.data;

      // Normalize workflows to have similar structure as models
      const normalizedWorkflows = workflowItems.map(w => ({
        ...w,
        model_id: w.workflow_definition_id, // Use workflow_definition_id as model_id for consistency
        model_name: w.name,
        model_type: 'workflow',
        model_cover_image_url: w.workflow_cover_image_url,
        isWorkflow: true // Flag to identify workflows
      }));

      // Combine models and workflows
      const allItems = [...modelItems, ...normalizedWorkflows];

      setModels(allItems);

      // Auto-select based on URL params, default model, or first item
      if (allItems.length > 0 && !selectedModel) {
        let targetItem = null;

        // Check for URL param selection
        if (modelId) {
          targetItem = modelItems.find(m => m.model_id === modelId);
        } else if (workflowId) {
          targetItem = normalizedWorkflows.find(w => w.workflow_definition_id === workflowId);
        }

        // Fall back to "Nano Banana 2 (PRO)" as default, then first item
        if (!targetItem) {
          const DEFAULT_MODEL_ID = '2a572435-d02b-4a9f-96d9-c34c5f0a95ef'; // Nano Banana 2 (PRO)
          targetItem = allItems.find(m => m.model_id === DEFAULT_MODEL_ID);
        }

        handleSelectModel(targetItem || allItems[0]);
      }
    } catch (err) {
      console.error('Failed to fetch models/workflows:', err);
      showNotification('Failed to load models', 'error');
    } finally {
      setModelsLoading(false);
    }
  };

  // ─── param classification helpers ──────────────────────────────

  // Detect the "primary prompt" param semantically (not by hardcoded name)
  const detectPrimaryPromptParam = (params) => {
    if (!params) return null;
    const textLike = params.filter(p => {
      if (p.is_private || p.allowed_values) return false;
      if (p.data_type !== 'string') return false;
      const n = p.parameter_name.toLowerCase();
      return !n.includes('negative') && !isMediaName(n) && !n.includes('webhook');
    });
    // Priority: positive_prompt > prompt > required text param > first text param
    return textLike.find(p => p.parameter_name === 'positive_prompt')
      || textLike.find(p => p.parameter_name === 'prompt')
      || textLike.find(p => p.parameter_name === 'text')
      || textLike.find(p => Number(p.is_required) === 1)
      || textLike[0]
      || null;
  };

  // Classify all params of a model into semantic buckets
  const classifyParams = (params) => {
    if (!params) return { primaryPrompt: null, negativePrompt: null, otherText: [], images: [], selects: [], numerics: [] };
    const primaryPrompt = detectPrimaryPromptParam(params);
    const negativePrompt = params.find(p => !p.is_private && p.data_type === 'string' && !p.allowed_values && p.parameter_name.toLowerCase().includes('negative'));
    // All media kinds, not just images — on a model switch a reference VIDEO
    // should transfer to the new model's video slot, not be silently dropped.
    // Kept under the `images` key because the transfer logic below and its
    // callers read that name.
    const images = params.filter(p => !!classifyMediaParam(p));
    const selects = params.filter(p => !p.is_private && p.allowed_values && p.data_type === 'string'
      && !images.find(ip => ip.parameter_name === p.parameter_name));
    const numerics = params.filter(p => !p.is_private && ['integer','number','float','boolean'].includes(p.data_type));
    const otherText = params.filter(p => !p.is_private && p.data_type === 'string' && !p.allowed_values
      && p !== primaryPrompt && p !== negativePrompt
      && !images.find(ip => ip.parameter_name === p.parameter_name));
    return { primaryPrompt, negativePrompt, otherText, images, selects, numerics };
  };

  // ─── Smart model switching ──────────────────────────────────────
  // Transfers params semantically by role, not by hardcoded name lists.

  const handleSelectModel = async (item) => {
    if (!item) return;

    // Snapshot current state at switch time (closures capture stale state otherwise)
    const currentTextInputs = textInputs;
    const currentUploadedImages = uploadedImages;
    const currentSettings = settings;
    const oldParams = classifyParams(selectedModel?.parameters);

    const doTransfer = (newItem) => {
      const newParams = classifyParams(newItem.parameters);
      const newText = {};
      const newImages = {};

      // ── 1. Primary prompt → primary prompt ──
      const oldPrimaryValue = oldParams.primaryPrompt
        ? (currentTextInputs[oldParams.primaryPrompt.parameter_name] || '')
        : '';
      if (oldPrimaryValue && newParams.primaryPrompt) {
        newText[newParams.primaryPrompt.parameter_name] = oldPrimaryValue;
      }

      // ── 2. Negative prompt → negative prompt ──
      if (oldParams.negativePrompt && newParams.negativePrompt) {
        const negVal = currentTextInputs[oldParams.negativePrompt.parameter_name] || '';
        if (negVal) newText[newParams.negativePrompt.parameter_name] = negVal;
      }

      // ── 3. Other text params — transfer by exact name match ──
      oldParams.otherText.forEach(oldP => {
        const val = currentTextInputs[oldP.parameter_name];
        if (!val) return;
        const match = newParams.otherText.find(np => np.parameter_name === oldP.parameter_name);
        if (match) newText[match.parameter_name] = val;
      });

      // ── 4. Select params — transfer by name + value must be valid in new model ──
      oldParams.selects.forEach(oldP => {
        const val = currentTextInputs[oldP.parameter_name];
        if (!val) return;
        const match = newParams.selects.find(np => np.parameter_name === oldP.parameter_name);
        if (!match) return;
        const allowed = parseAllowedValues(match.allowed_values);
        if (allowed.map(String).includes(String(val))) {
          newText[match.parameter_name] = val;
        } else if (allowed.length > 0) {
          newText[match.parameter_name] = allowed[0]; // fallback to first valid
        }
      });

      // ── 5. Media params — by exact name, then by position within the SAME KIND ──
      const oldImageEntries = Object.entries(currentUploadedImages || {});
      const shapeFor = (param, value) => {
        const m = classifyMediaParam(param);
        return m?.isArray
          ? (Array.isArray(value) ? value : [value])
          : (Array.isArray(value) ? (value[0] || null) : value);
      };
      oldImageEntries.forEach(([paramName, value]) => {
        if (!value) return;
        const newImageParam = newParams.images.find(np => np.parameter_name === paramName);
        if (newImageParam) {
          // Exact name match — convert single↔array as needed
          newImages[paramName] = shapeFor(newImageParam, value);
        }
      });
      // If nothing matched by name and exactly one slot was filled, fall back to
      // position — but only onto a slot of the SAME media kind. Without the kind
      // check, switching models would drop a reference video into an image slot
      // and the provider would reject it.
      if (Object.keys(newImages).length === 0 && oldImageEntries.length === 1 && newParams.images.length > 0) {
        const [oldName, value] = oldImageEntries[0];
        const oldParam = oldParams.images.find(p => p.parameter_name === oldName);
        const oldKind = oldParam ? classifyMediaParam(oldParam)?.kind : null;
        // When the old kind is unknown, keep the previous behaviour and fall
        // back to the first slot rather than dropping the value entirely.
        const target = oldKind
          ? newParams.images.find(np => classifyMediaParam(np)?.kind === oldKind)
          : newParams.images[0];
        if (value && target) {
          newImages[target.parameter_name] = shapeFor(target, value);
        }
      }

      // ── 6. Dimension / ratio / quality ──
      const newDimConfig = newItem.resolution?.dimension_presets;
      const newAspectParam = newParams.selects.find(p => p.parameter_name.toLowerCase() === 'aspect_ratio');
      const newSettingsPartial = {};

      if (newDimConfig?.ratios?.length) {
        // New model uses dimension_presets system
        const transferRatio = currentSettings.ratio || newDimConfig.default_ratio;
        newSettingsPartial.ratio = newDimConfig.ratios.includes(transferRatio)
          ? transferRatio
          : newDimConfig.default_ratio || newDimConfig.ratios[0];
        if (newDimConfig.qualities?.length) {
          const transferQuality = currentSettings.quality || newDimConfig.default_quality;
          newSettingsPartial.quality = newDimConfig.qualities.includes(transferQuality)
            ? transferQuality
            : newDimConfig.default_quality || newDimConfig.qualities[0];
        } else {
          newSettingsPartial.quality = null;
        }
      } else if (newAspectParam) {
        // New model uses an aspect_ratio select param
        const allowed = parseAllowedValues(newAspectParam.allowed_values);
        const want = currentSettings.ratio
          || currentTextInputs[newAspectParam.parameter_name]
          || allowed[0];
        newText[newAspectParam.parameter_name] = allowed.map(String).includes(String(want))
          ? want
          : allowed[0];
        newSettingsPartial.ratio = null;
        newSettingsPartial.quality = null;
      } else {
        // New model has no dimension system — clear
        newSettingsPartial.ratio = null;
        newSettingsPartial.quality = null;
      }

      // ── 7. Numeric/boolean params — transfer via textInputs (no settings mapping) ──
      // Alias groups: functionally equivalent params across models
      const NUMERIC_ALIASES = [
        ['steps','num_inference_steps'],
        ['cfg_scale','guidance_scale'],
        ['seed'],
        ['num_images','num_outputs','batch_size'],
        ['sampler','scheduler'],
      ];
      const newParamNames = new Set((newItem.parameters || []).map(p => p.parameter_name));

      // For each alias group, find if old model had a value and new model has the concept
      NUMERIC_ALIASES.forEach(group => {
        // Find value from old textInputs (any member of the group)
        let oldVal;
        for (const alias of group) {
          if (currentTextInputs[alias] !== undefined) { oldVal = currentTextInputs[alias]; break; }
        }
        if (oldVal === undefined) return;
        // Find the new model's matching param (any member of the group)
        for (const alias of group) {
          if (newParamNames.has(alias) && newText[alias] === undefined) {
            newText[alias] = oldVal;
            break;
          }
        }
      });

      // ── 8. Apply model defaults for params NOT yet covered by transfer ──
      (newItem.parameters || []).forEach(p => {
        if (p.is_private || p.default_value === undefined || p.default_value === null) return;
        if (newText[p.parameter_name] !== undefined) return; // already transferred
        const dt = p.data_type;
        // Numeric/boolean → to textInputs (will be read by ZenControlPanel's advancedParams)
        if (['integer','number','float','boolean'].includes(dt)) {
          newText[p.parameter_name] = p.default_value;
        }
        // String with allowed_values (select) → to textInputs
        if (dt === 'string' && p.allowed_values) {
          newText[p.parameter_name] = p.default_value;
        }
      });

      // ── Apply all transfers ──
      setTextInputs(newText);
      setUploadedImages(Object.keys(newImages).length > 0 ? newImages : null);
      setSettings(prev => {
        const next = { ...prev, ...newSettingsPartial };
        // Remove keys explicitly set to undefined (model doesn't support)
        Object.keys(next).forEach(k => { if (next[k] === undefined) delete next[k]; });
        return next;
      });
      setSelectedModel(newItem);
    };

    if (item.isWorkflow && !item.parameters) {
      setLoadingDetails(true);
      try {
        const { data } = await getUroSDK().getWorkflowById(item.workflow_definition_id);
        const enriched = { ...item, parameters: data.parameters || [] };
        doTransfer(enriched);
        setModels(prev => prev.map(m =>
          m.workflow_definition_id === item.workflow_definition_id ? enriched : m
        ));
      } catch (err) {
        console.error('Failed to fetch workflow details:', err);
        setSelectedModel(item);
      } finally {
        setLoadingDetails(false);
      }
    } else {
      doTransfer(item);
    }
  };

  // Get the main text parameters from the selected model/workflow
  const getTextParams = () => {
    if (!selectedModel?.parameters) return [];

    return selectedModel.parameters.filter(p => {
      const name = p.parameter_name.toLowerCase();
      const isText = p.data_type === 'string' && !isMediaName(name) && !name.includes('webhook');
      return isText;
    });
  };

  // Handle reuse job state from gallery navigation or URL params.
  // history.replaceState below does NOT clear React Router's in-memory
  // location.state, so both branches are ref-gated on the last value applied —
  // otherwise switching models re-splats the old job onto the new model.
  const appliedImageUrlRef = useRef(null);
  const appliedReuseStateRef = useRef(null);
  useEffect(() => {
    if (!selectedModel?.parameters) return;

    // Handle URL search params (e.g., ?image_url=...)
    const imageUrlParam = searchParams.get('image_url');
    if (imageUrlParam && appliedImageUrlRef.current !== imageUrlParam) {
      appliedImageUrlRef.current = imageUrlParam;
      // ?image_url= carries an IMAGE, so only image slots are valid targets.
      const imageParamsForUrl = selectedModel.parameters.filter(
        p => classifyMediaParam(p)?.kind === 'image'
      );

      if (imageParamsForUrl.length > 0) {
        const targetParam = imageParamsForUrl[0];
        const isArray = targetParam.data_type === 'object[]' ||
          (targetParam.allowed_max_value && Number(targetParam.allowed_max_value) > 1);
        setUploadedImages({
          [targetParam.parameter_name]: isArray ? [imageUrlParam] : imageUrlParam
        });
      } else {
        setUploadedImages({ image_urls: [imageUrlParam] });
      }
      showNotification('Image loaded from URL', 'success');
      // Clear the URL param after applying
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Handle reuse job state from gallery navigation
    if (reuseJobState && appliedReuseStateRef.current !== reuseJobState) {
      appliedReuseStateRef.current = reuseJobState;
      const { inputParams, inputImages, inputTexts } = reuseJobState;

      // Apply text inputs from inputParams
      if (inputParams && typeof inputParams === 'object') {
        const textValues = {};
        const settingsValues = {};
        const imageValues = {};

        Object.entries(inputParams).forEach(([key, value]) => {
          const param = selectedModel.parameters?.find(p => p.parameter_name === key);
          if (!param) return;

          // Classifier, not the name test: `isMediaName` also matches
          // non-uploads like the integer `image_quality`, which would then land
          // in the uploaded-media map and break kind detection on model switch.
          const isImageKey = !!classifyMediaParam(param);
          const isTextKey = param.data_type === 'string' && !isImageKey;
          const isNumericKey = ['number', 'integer', 'float'].includes(param.data_type);

          if (isImageKey && value) {
            imageValues[key] = value;
          } else if (isTextKey && value) {
            textValues[key] = value;
          } else if (isNumericKey && value !== undefined) {
            settingsValues[key] = value;
          }
        });

        if (Object.keys(textValues).length > 0) {
          setTextInputs(prev => ({ ...prev, ...textValues }));
        }
        if (Object.keys(settingsValues).length > 0) {
          setSettings(prev => ({ ...prev, ...settingsValues }));
        }
        if (Object.keys(imageValues).length > 0) {
          setUploadedImages(imageValues);
        }
      }

      // Apply input images
      if (inputImages && typeof inputImages === 'object') {
        setUploadedImages(prev => ({ ...prev, ...inputImages }));
      }

      // Apply input texts
      if (inputTexts && typeof inputTexts === 'object') {
        setTextInputs(prev => ({ ...prev, ...inputTexts }));
      }

      showNotification('Parameters loaded from previous job', 'success');
      // Clear the navigation state after applying
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?.model_id, !!selectedModel?.parameters, reuseJobState, searchParams]);

  // Build model-specific params
  const buildModelParams = (model, settingsObj, inputImages, textInputsObj) => {
    const params = {};
    const isWorkflow = !!(model.isWorkflow || model.workflow_definition_id);

    // Get available params from model/workflow
    const availableParams = model.parameters || [];
    const paramNames = availableParams.map(p => p.parameter_name);

    // Apply default values from parameter configs (for both models and workflows)
    availableParams.forEach(param => {
      if (param.is_private || Number(param.is_private) === 1) return;

      // Apply default_value if set
      if (param.default_value !== undefined && param.default_value !== null && param.default_value !== '') {
        params[param.parameter_name] = param.default_value;
      }
      // For params with allowed_values (required or not), use first allowed value as default
      else if (param.allowed_values) {
        let values = param.allowed_values;
        if (typeof values === 'string') {
          try {
            const parsed = JSON.parse(values);
            // JSON.parse("24") returns number 24, wrap in array
            values = Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            values = values.split(',').map(v => v.trim());
          }
        }
        if (Array.isArray(values) && values.length > 0) {
          params[param.parameter_name] = values[0];
        } else if (values !== null && values !== undefined) {
          params[param.parameter_name] = values;
        }
      }
    });

    // Add all text inputs to params (overrides defaults)
    if (textInputsObj && typeof textInputsObj === 'object') {
      Object.entries(textInputsObj).forEach(([paramName, value]) => {
        if (value !== undefined && value !== null && value !== '' && paramNames.includes(paramName)) {
          params[paramName] = typeof value === 'string' ? value.trim() : value;
        }
      });
    }

    // Dimensions - send ratio+quality (backend resolves to provider-specific params)
    if (!isWorkflow) {
      const dp = model.resolution?.dimension_presets;
      if (dp?.lookup) {
        // New structured format: send ratio + quality
        const ratio = settingsObj.ratio || dp.default_ratio;
        const quality = settingsObj.quality || dp.default_quality;
        if (ratio) params.ratio = ratio;
        if (quality && quality !== 'default') params.quality = quality;
      } else if (settingsObj.width && settingsObj.height) {
        // Legacy fallback: send raw width/height
        if (paramNames.includes('width')) params.width = settingsObj.width;
        if (paramNames.includes('height')) params.height = settingsObj.height;
      }
    }

    // Input image handling - uploadedImages is now { paramName: url/[urls] }
    if (inputImages && typeof inputImages === 'object') {
      Object.entries(inputImages).forEach(([paramName, value]) => {
        if (value && paramNames.includes(paramName)) {
          params[paramName] = value;
        }
      });
    }

    return params;
  };

  // Set by handleGenerate so the results pane jumps to what we just submitted.
  // The old effect fired on every generations.length change, which yanked the
  // user to the top every time "Load older" prepended a page.
  const focusNewestRef = useRef(false);

  const handleGenerate = async () => {
    if (!selectedModel) {
      showNotification('Please select a model/workflow', 'warning');
      return;
    }

    // Check if at least one text input has content. textInputs holds numbers
    // and booleans too, so type-check before treating a value as text.
    const textParams = getTextParams();
    const hasTextInput = textParams.length === 0 ||
      textParams.some(p => isFilled(textInputs[p.parameter_name]));

    if (!hasTextInput) {
      showNotification('Please enter some text', 'warning');
      return;
    }

    setIsGenerating(true);

    // Everything below runs inside try/finally: any throw used to leave the
    // Generate button stuck on "Generating…" forever.
    try {
      const numRuns = Math.max(1, Math.min(4, Number(batchCount) || 1));

      // Display prompt — first *string* value with content
      const displayPrompt = Object.values(textInputs).find(v => typeof v === 'string' && v.trim()) || '';

      const isWorkflow = !!(selectedModel.isWorkflow || selectedModel.workflow_definition_id);
      const baseParams = buildModelParams(selectedModel, settings, uploadedImages, textInputs);

      // The Batch control decides how many runs happen. Pin any provider-side
      // count param to 1 rather than deleting it — deleting would drop a
      // required param and 400 the request, while leaving it would multiply
      // (batch 4 × num_images 4 = 16 charged outputs).
      NATIVE_BATCH_PARAMS.forEach(k => { if (k in baseParams) baseParams[k] = 1; });

      // A pinned seed would make every run in a batch identical — walk it.
      const seedRaw = baseParams.seed;
      const seedIsPinned = seedRaw !== undefined && seedRaw !== '' && Number(seedRaw) >= 0 && !Number.isNaN(Number(seedRaw));

      const stamp = Date.now();
      const runs = Array.from({ length: numRuns }, (_, i) => {
        const p = { ...baseParams };
        if (seedIsPinned && numRuns > 1) p.seed = Number(seedRaw) + i;
        return p;
      });

      const newGenerations = runs.map((p, i) => ({
        id: `gen_${stamp}_${i}`,
        jobId: null,
        prompt: displayPrompt,
        textInputs: { ...textInputs },
        model: selectedModel,
        inputImages: uploadedImages,
        settings: { ...settings },
        input_params: p,
        status: 'submitting',
        images: [],
        batchIndex: i,
        batchTotal: numRuns,
        isWorkflow,
        // One timestamp for the whole batch; batchIndex is the tiebreak in
        // `ordered`, so a batch always reads 1,2,3,4 in either sort direction.
        createdAt: new Date(stamp),
      }));

      setGenerations(prev => [...prev, ...newGenerations]);
      focusNewestRef.current = true;
      if (isCompact) setControlsOpen(false);

      let successCount = 0;
      for (let i = 0; i < runs.length; i++) {
        const generationId = newGenerations[i].id;

        try {
          let response;
          if (isWorkflow) {
            // Run workflow - params go in input_params
            response = await getUroSDK().runWorkflow({
              workflow_definition_id: selectedModel.workflow_definition_id,
              input_params: runs[i]
            });
          } else {
            // Run model - params spread directly
            response = await getUroSDK().runModel({
              model_id: selectedModel.model_id,
              ...runs[i]
            });
          }

          // Get job ID - workflows return workflow_job_id, models return image_job_id
          const jobId = response?.data?.workflow_job_id || response?.workflow_job_id ||
                        response?.data?.image_job_id || response?.image_job_id;

          // Check if API returned an immediate failure (validation / provider error)
          const responseStatus = response?.data?.status || response?.status;
          const responseError = response?.data?.error || response?.error;

          if (responseStatus === 'failed') {
            const msg = typeof responseError === 'string' ? responseError : (responseError?.message || 'Generation failed');
            setGenerations(prev => prev.map(gen =>
              gen.id === generationId
                ? { ...gen, jobId: jobId || null, status: 'failed', error: msg, isWorkflow }
                : gen
            ));
            showNotification(`Generation failed: ${msg}`, 'error');
            continue; // Skip to next generation
          }

          if (!jobId) {
            setGenerations(prev => prev.map(gen =>
              gen.id === generationId
                ? { ...gen, status: 'failed', error: 'The API accepted the request but returned no job id', isWorkflow }
                : gen
            ));
            continue;
          }

          // Update generation with real job ID
          setGenerations(prev => prev.map(gen =>
            gen.id === generationId
              ? { ...gen, id: jobId, jobId, status: 'processing', isWorkflow }
              : gen
          ));

          successCount++;
        } catch (err) {
          console.error(`Generation ${i + 1} failed:`, err);

          // API returns { error: { message: "..." } } for validation errors
          const errorMessage = err?.response?.data?.error?.message ||
                              err?.response?.data?.message ||
                              err?.response?.data?.error ||
                              err?.message ||
                              'Request failed - please try again';

          setGenerations(prev => prev.map(gen =>
            gen.id === generationId
              ? { ...gen, status: 'failed', error: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage) }
              : gen
          ));

          showNotification(`Generation failed: ${errorMessage}`, 'error');
        }
      }

      if (successCount > 0) {
        showNotification(`${successCount} generation${successCount > 1 ? 's' : ''} started`, 'success');
      } else if (numRuns > 1) {
        showNotification('All generations failed', 'error');
      }
    } catch (err) {
      // Anything unexpected (bad params, a throw inside buildModelParams, …)
      console.error('Generate failed before submit:', err);
      showNotification(err?.message || 'Could not start the generation', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  // Format cost
  const formatCost = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '~';
    const num = Number(v);
    if (num >= 1) return num.toFixed(2);
    if (num >= 0.01) return num.toFixed(4);
    return num.toPrecision(2);
  };

  // Media params for the "Use as input" pill on generated results.
  // IMAGE-ONLY on purpose: this list is consumed by GenerationCard /
  // ZenGalleryView / ZenGenerationModal, which offer to feed a finished
  // generation back in as an input. Offering a generated still as the target
  // for a `reference_video_urls` slot would send an image URL where the
  // provider expects a video and fail at the gateway. Video results become
  // eligible once those surfaces can tell a video result from an image one.
  //
  // Uses the shared classifier so it can no longer drift from ZenControlPanel,
  // and it now honours is_private (the old inline copy did not, so private
  // params leaked into the pill).
  const imageParams = useMemo(() => {
    if (!selectedModel?.parameters) return [];

    return selectedModel.parameters.reduce((acc, p) => {
      const m = classifyMediaParam(p);
      if (!m || m.kind !== 'image') return acc;
      acc.push({
        paramName: p.parameter_name,
        // Use a curated parameter_title verbatim — formatParamLabel strips
        // trailing digits, which would turn "Image 2" into "Image".
        title: p.parameter_title || formatParamLabel(p.parameter_name),
        isArray: m.isArray,
        maxCount: m.max,
        minCount: m.min,
      });
      return acc;
    }, []);
  }, [selectedModel]);

  // Use image as input - handles both single values and arrays
  const handleUseAsInput = (imageUrl, paramName = null) => {
    if (!imageUrl) return;

    // No image slot on this model (e.g. a video-only model like Video Loop or
    // Kling VIDEO O1 Pro). The old `|| 'image'` fallback invented a param name
    // that no model has: buildModelParams then dropped it, but the user still
    // got a success toast and a phantom "1 uploaded" count.
    if (imageParams.length === 0) {
      showNotification('This model has no image input', 'warning');
      return false;
    }

    // Find the target param info
    const targetParamName = paramName || imageParams[0].paramName;
    const paramInfo = imageParams.find(p => p.paramName === targetParamName);
    const isArray = paramInfo?.isArray || false;
    const maxCount = paramInfo?.maxCount || 10;
    const title = paramInfo?.title || 'Input';

    setUploadedImages(prev => {
      const current = prev || {};
      const currentValue = current[targetParamName];

      if (isArray) {
        // Handle array params - add to array instead of replacing
        let newArray = [];

        if (Array.isArray(currentValue)) {
          newArray = [...currentValue];
        } else if (currentValue) {
          // Convert existing single value to array
          newArray = [currentValue];
        }

        newArray.push(imageUrl);

        // If exceeds max, remove oldest (first) items
        while (newArray.length > maxCount) {
          newArray.shift();
        }

        return { ...current, [targetParamName]: newArray };
      }
      // Single value param - replace
      return { ...current, [targetParamName]: imageUrl };
    });

    if (isCompact) setControlsOpen(true);
    showNotification(isArray ? `Image added to ${title}` : `Image set as ${title}`, 'success');
    return true;
  };

  // Use text output as prompt
  const handleUseAsPrompt = (text) => {
    if (!text) return;

    const textParams = getTextParams();
    if (textParams.length > 0) {
      const targetParam = textParams[0].parameter_name;
      setTextInputs(prev => ({ ...prev, [targetParam]: text }));
      if (isCompact) setControlsOpen(true);
      showNotification('Text set as prompt', 'success');
    }
  };

  // Extract frame from video and use as input
  const handleUseVideoFrame = async (videoUrl, frameType = 'first') => {
    if (!videoUrl) return;

    let blobUrl = null;

    try {
      showNotification(`Extracting ${frameType} frame...`, 'info');

      // Use backend proxy to bypass CORS restrictions
      const proxyUrl = `/media/proxy?url=${encodeURIComponent(videoUrl)}`;
      const videoResponse = await secureAxios.get(proxyUrl, { responseType: 'blob' });

      // Verify we got a video blob, not an error response
      const blob = videoResponse.data;
      if (!blob || blob.type?.includes('json') || blob.size < 1000) {
        throw new Error('Proxy returned invalid video data');
      }

      blobUrl = URL.createObjectURL(blob);

      // Create a video element to extract the frame
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      // Wait for video to be ready
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Video load timeout')), 30000);

        video.onloadeddata = () => {
          clearTimeout(timeout);
          if (frameType === 'last') {
            video.currentTime = Math.max(0, video.duration - 0.1);
          } else {
            video.currentTime = 0.1;
          }
        };

        video.onseeked = () => {
          clearTimeout(timeout);
          resolve();
        };

        video.onerror = () => {
          clearTimeout(timeout);
          reject(new Error(`Video error: ${video.error?.message || 'Unknown'}`));
        };

        video.src = blobUrl;
        video.load();
      });

      // Ensure video has valid dimensions
      if (!video.videoWidth || !video.videoHeight) {
        throw new Error('Video has no valid dimensions');
      }

      // Draw frame to canvas
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Convert to blob
      const frameBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

      if (!frameBlob) {
        throw new Error('Failed to create frame blob');
      }

      // Upload the frame to get a proper URL
      showNotification('Uploading frame...', 'info');
      const uploadRes = await imageUploadActions.uploadImage(frameBlob);

      // API returns { success: true, image_url: '...' }
      const uploadedUrl = uploadRes.image_url || uploadRes.data?.url;
      if (!uploadRes.success || !uploadedUrl) {
        throw new Error('Failed to upload frame');
      }

      // Only claim success if the frame actually landed in a slot.
      // showNotification is single-slot state, so an unconditional success
      // toast here would overwrite handleUseAsInput's warning before it
      // rendered — the user would be told "frame ready" on a video-only model
      // where nothing was applied.
      if (!handleUseAsInput(uploadedUrl)) return;

      showNotification(`${frameType === 'first' ? 'First' : 'Last'} frame ready`, 'success');
    } catch (err) {
      console.error('Failed to extract frame:', err);
      showNotification(`Failed to extract frame: ${err.message}`, 'error');
    } finally {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  };

  // Re-run with same params
  const handleRerun = async (generation) => {
    const inputParams = generation.input_params || {};
    const genModel = generation.model || {};

    // 1. Find the full model from models list using model_id or workflow_definition_id
    const genModelId = genModel.model_id;
    const genWorkflowId = genModel.workflow_definition_id;

    let fullModel = null;
    if (genModelId) {
      fullModel = models.find(m => m.model_id === genModelId);
    } else if (genWorkflowId) {
      fullModel = models.find(m => m.workflow_definition_id === genWorkflowId);
    }

    if (!fullModel) {
      showNotification('That model is not available in Zen any more', 'warning');
      return;
    }

    // 2. Select the model (fetches workflow parameters if needed)
    await handleSelectModel(fullModel);

    // 3. Restore ALL input_params into the right state buckets.
    // No hardcoded key lists — use the actual model params to classify.
    const newTextInputs = {};
    const newUploadedImages = {};
    const newSettings = { ratio: settings.ratio, quality: settings.quality }; // keep only meta-level

    const modelParams = fullModel?.parameters || [];
    const imageParamNames = new Set(modelParams
      .filter(p => !!classifyMediaParam(p))
      .map(p => p.parameter_name));

    Object.entries(inputParams).forEach(([key, value]) => {
      if (['webhookURL', 'customTaskUUID'].includes(key)) return;
      if (value === undefined || value === null) return;

      const isImageUrl = (typeof value === 'string' && value.startsWith('http'))
        || (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' && value[0].startsWith('http'));

      if (imageParamNames.has(key) || isImageUrl) {
        newUploadedImages[key] = value;
      } else {
        // Everything else (strings, numbers, booleans) → textInputs by param name
        newTextInputs[key] = value;
      }
    });

    // Restore ratio/quality — they live in settings, not textInputs
    if (inputParams.ratio) newSettings.ratio = inputParams.ratio;
    if (inputParams.quality) newSettings.quality = inputParams.quality;
    if (inputParams.aspect_ratio && !newSettings.ratio) {
      newSettings.ratio = inputParams.aspect_ratio;
    }

    setSettings(newSettings);
    setTextInputs(newTextInputs);
    setUploadedImages(Object.keys(newUploadedImages).length > 0 ? newUploadedImages : null);

    if (isCompact) setControlsOpen(true);
    showNotification('Parameters loaded — press Generate to run', 'info');
  };

  // Edit image using Nano Banana Pro model
  const NANO_BANANA_PRO_MODEL_ID = '2a572435-d02b-4a9f-96d9-c34c5f0a95ef';

  const handleEditImage = async (imageUrl) => {
    if (!imageUrl) return;

    const nanoBananaModel = models.find(m => m.model_id === NANO_BANANA_PRO_MODEL_ID);

    if (!nanoBananaModel) {
      // Model not in zen_enabled list, just use as input with current model
      handleUseAsInput(imageUrl);
      showNotification('Edit model not available — image set as input', 'warning');
      return;
    }

    await handleSelectModel(nanoBananaModel);

    // handleSelectModel resets uploadedImages, so the image has to be applied
    // after it settles — do it from the model we already have in hand.
    // Edit-image hands over an image URL, so restrict to image slots.
    const modelImageParams = nanoBananaModel.parameters?.filter(
      p => classifyMediaParam(p)?.kind === 'image'
    ) || [];

    if (modelImageParams.length > 0) {
      const targetParam = modelImageParams[0];
      const isArray = targetParam.data_type === 'object[]' || (targetParam.allowed_max_value && Number(targetParam.allowed_max_value) > 1);
      setUploadedImages({ [targetParam.parameter_name]: isArray ? [imageUrl] : imageUrl });
    } else {
      setUploadedImages({ image_urls: [imageUrl] });
    }

    if (isCompact) setControlsOpen(true);
    showNotification('Image loaded for editing with Nano Banana Pro', 'success');
  };

  // ─── Likes ────────────────────────────────────────────────────────
  const handleToggleLike = useCallback(async (generation) => {
    const jobId = generation.jobId || generation.id;
    if (!jobId || String(jobId).startsWith('gen_')) return; // not submitted yet
    const jobType = (generation.isWorkflow || generation.model?.isWorkflow) ? 'workflow' : 'image';

    const wasLiked = likedIds.has(jobId);
    setLikedIds(prev => {
      const next = new Set(prev);
      if (wasLiked) next.delete(jobId); else next.add(jobId);
      return next;
    });
    setLikePendingIds(prev => new Set(prev).add(jobId));

    try {
      const result = await jobActions.toggleLike(jobId, jobType);
      if (result?.liked !== undefined) {
        setLikedIds(prev => {
          const next = new Set(prev);
          if (result.liked) next.add(jobId); else next.delete(jobId);
          return next;
        });
      }
    } catch {
      // revert
      setLikedIds(prev => {
        const next = new Set(prev);
        if (wasLiked) next.add(jobId); else next.delete(jobId);
        return next;
      });
      showNotification('Could not save that like', 'error');
    } finally {
      setLikePendingIds(prev => { const next = new Set(prev); next.delete(jobId); return next; });
    }
  }, [likedIds, showNotification]);

  // ─── Before/after ─────────────────────────────────────────────────
  const handleCompare = useCallback((generation, url) => {
    const outs = getOutputs(generation).filter(o => o.url && o.type !== 'video' && o.type !== 'text').map(o => o.url);
    const inputs = getInputImages(generation).filter(u => !outs.includes(u));
    if (inputs.length === 0) {
      showNotification('This generation had no input image to compare against', 'info');
      return;
    }
    const idx = Math.max(0, outs.indexOf(url));
    setCompareState({ images: outs.length ? outs : [url], inputImages: inputs, initialIndex: idx });
  }, [showNotification]);

  const handleDismiss = useCallback((generation) => {
    setGenerations(prev => prev.filter(g => g.id !== generation.id));
  }, []);

  // ─── Load old generations (image jobs + workflow jobs) ─────────────
  const [oldGenerationsLoading, setOldGenerationsLoading] = useState(false);
  const [oldGenerationsPage, setOldGenerationsPage] = useState(1);
  const [hasMoreOldGenerations, setHasMoreOldGenerations] = useState(true);
  const loadingRef = useRef(false);

  const loadOldGenerations = async (reset = false) => {
    if (!reset && loadingRef.current) return;
    if (!reset && !hasMoreOldGenerations) return;

    const page = reset ? 1 : oldGenerationsPage;

    loadingRef.current = true;
    setOldGenerationsLoading(true);
    try {
      const params = { limit: 12, page, status: 'completed' };
      if (activeFilter !== 'all') params.job_type = activeFilter;
      if (modelFilter) params.model_id = modelFilter;
      const response = await listUnifiedJobs(params);

      const likedFromApi = [];

      const jobs = (response?.data?.items || [])
        .map(job => {
          const isWorkflow = job.job_type === 'workflow';

          // Extract results based on job type
          let results;
          if (isWorkflow) {
            results = Array.isArray(job.results?.results) ? job.results.results :
                      Array.isArray(job.results) ? job.results : [];
          } else {
            results = Array.isArray(job.results) ? job.results : [];
          }

          // A text result's `content` is the TEXT, not a URL. Falling through to
          // it unconditionally put prose into `url`, which then reached an
          // <img src> and fired a request for "A text output from a workflow…".
          const outputs = results.map(r => {
            const type = r?.content_type || 'image';
            const url = r?.external_url || r?.image_url || r?.url
              || (type !== 'text' ? r?.content : null) || null;
            return { url, content: type === 'text' ? (r?.content ?? null) : null, type };
          }).filter(o => o.url || o.content);
          const images = outputs.filter(o => o.type === 'image' && o.url).map(o => o.url);

          const id = isWorkflow ? job.workflow_job_id : job.image_job_id;
          if (job.is_liked) likedFromApi.push(id);

          if (isWorkflow) {
            return {
              id,
              jobId: id,
              jobType: 'workflow',
              model: {
                model_name: job.workflow_definition_name || 'Workflow',
                workflow_definition_id: job.workflow_definition_id,
                isWorkflow: true
              },
              status: 'completed',
              images,
              outputs,
              input_params: job.input_params || {},
              duration: job.duration,
              cost: job.cost_to_client,
              createdAt: new Date(job.created_at),
              isOld: true,
              isWorkflow: true
            };
          }
          return {
            id,
            jobId: id,
            jobType: 'image',
            prompt: job.input_params?.prompt || job.input_params?.positive_prompt || '',
            model: { model_name: job.model_name || 'Unknown', model_id: job.model_id },
            status: 'completed',
            images,
            outputs,
            input_params: job.input_params || {},
            duration: job.duration,
            cost: job.cost_to_client,
            createdAt: new Date(job.created_at),
            isOld: true
          };
        })
        // Was `job.images.length > 0`, which silently dropped every text-only
        // and video-only job from the feed.
        .filter(job => job.outputs.length > 0);

      if (likedFromApi.length) {
        setLikedIds(prev => { const next = new Set(prev); likedFromApi.forEach(id => next.add(id)); return next; });
      }

      // Jobs are already sorted by created_at desc from API, reverse for oldest first
      const allJobs = jobs.sort((a, b) => a.createdAt - b.createdAt);

      // Check if there are more pages
      const pagination = response?.data?.pagination;
      const hasMore = pagination ? page < pagination.totalPages : jobs.length === 12;
      setHasMoreOldGenerations(hasMore);

      if (allJobs.length > 0) {
        // De-dupe: a job already in the session must not appear twice
        setGenerations(prev => {
          const base = reset ? prev.filter(g => !g.isOld) : prev;
          const known = new Set(base.map(g => g.jobId || g.id));
          const fresh = allJobs.filter(j => !known.has(j.id));
          return [...fresh, ...base];
        });
        setOldGenerationsPage(page + 1);
        showNotification(`Loaded ${allJobs.length} generation${allJobs.length > 1 ? 's' : ''}`, 'success');
      } else if (reset) {
        showNotification('No recent generations found', 'info');
      } else {
        setHasMoreOldGenerations(false);
        showNotification('No more generations', 'info');
      }
    } catch (err) {
      console.error('Failed to load old generations:', err);
      showNotification('Failed to load generations', 'error');
    } finally {
      loadingRef.current = false;
      setOldGenerationsLoading(false);
    }
  };

  // Reload old generations when filter changes
  useEffect(() => {
    setGenerations(prev => prev.filter(g => !g.isOld));
    setOldGenerationsPage(1);
    setHasMoreOldGenerations(true);
    loadOldGenerations(true);
  }, [activeFilter, modelFilter]); // eslint-disable-line

  /* ── Display ordering ────────────────────────────────────────────
     `generations` is kept oldest→newest internally. Sorting explicitly by
     createdAt (rather than reversing the array) is what makes the grid read
     left-to-right, top-to-bottom in a stable order. */
  const ordered = useMemo(() => {
    const copy = [...generations];
    copy.sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      if (ta !== tb) return sortOrder === 'newest' ? tb - ta : ta - tb;
      // Same timestamp = same batch — keep it in submit order both ways round.
      return (a.batchIndex ?? 0) - (b.batchIndex ?? 0);
    });
    return copy;
  }, [generations, sortOrder]);

  /* Flatten to one item per OUTPUT so a 4-image job shows 4 tiles instead of
     hiding three behind a badge. Pending/failed jobs contribute one item. */
  const flatItems = useMemo(() => {
    const out = [];
    ordered.forEach(gen => {
      const outs = getOutputs(gen).filter(o => o.url || o.content);
      if (outs.length === 0) {
        out.push({ key: String(gen.id), gen, output: null, outputIndex: 0, outputCount: 0 });
        return;
      }
      outs.forEach((o, i) => {
        out.push({ key: `${gen.id}::${i}`, gen, output: o, outputIndex: i, outputCount: outs.length });
      });
    });
    return out;
  }, [ordered]);

  // Keep the gallery cursor inside the list as items load/unload
  useEffect(() => {
    setGalleryIndex(i => Math.min(Math.max(0, i), Math.max(0, flatItems.length - 1)));
  }, [flatItems.length]);

  // Jump to whatever we just submitted — scroll the grid/list, move the gallery
  // cursor. Runs only for our own submissions, never for "Load older".
  useEffect(() => {
    if (!focusNewestRef.current) return;
    focusNewestRef.current = false;
    if (sortOrder === 'newest' && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setGalleryIndex(sortOrder === 'newest' ? 0 : Math.max(0, flatItems.length - 1));
  }, [flatItems, sortOrder]);

  const activeGenId = viewMode === 'gallery' ? flatItems[Math.min(galleryIndex, flatItems.length - 1)]?.gen?.id : detailViewJob?.id;

  // Show loading indicator when loading a model/workflow from URL
  const isLoadingFromUrl = modelsLoading && (modelId || workflowId);

  const cardHandlers = {
    onUseAsInput: handleUseAsInput,
    onUseAsPrompt: handleUseAsPrompt,
    onUseVideoFrame: handleUseVideoFrame,
    onEditImage: handleEditImage,
    onToggleLike: handleToggleLike,
    onCompare: handleCompare,
    imageParams,
  };

  const controlPanel = (
    <ZenControlPanel
      selectedModel={selectedModel}
      modelsLoading={modelsLoading}
      onOpenModelSelector={() => setModelSelectorOpen(true)}
      onOpenSettings={() => setSettingsOpen(true)}
      textInputs={textInputs}
      setTextInputs={setTextInputs}
      uploadedImages={uploadedImages}
      setUploadedImages={setUploadedImages}
      settings={settings}
      setSettings={setSettings}
      isGenerating={isGenerating}
      loadingDetails={loadingDetails}
      onGenerate={handleGenerate}
      formatCost={formatCost}
      balance={balance}
      showNotification={showNotification}
      batchCount={batchCount}
      setBatchCount={setBatchCount}
      onCloseSheet={isCompact ? () => setControlsOpen(false) : undefined}
    />
  );

  return (
    <AppScreenWrapper>
    <div className="screen-root">
      <Sidebar balance={balance} name={clientName} email={clientEmail} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        <ScreenOrbs tone="orange" />
        <Topbar
          title={
            <span className="row" style={{ gap: 8, color: 'var(--text-3)', fontSize: 13, fontWeight: 500, letterSpacing: 0 }}>
              <span>Zen</span>
              <span style={{ color: 'var(--text-4)' }}>/</span>
              <span style={{ color: '#fff', fontWeight: 600 }}>
                {selectedModel?.model_name || selectedModel?.name || 'Select a model'}
              </span>
            </span>
          }
          name={clientName}
        />

      {/* Toast notification */}
      {notification && (
        <div style={{
          // Below the Topbar, and clear of antd's top-centred `message.error`
          // (session-expired) so the two never stack on top of each other.
          position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500, maxWidth: '90vw',
          background: notification.type === 'error' ? 'rgba(239,68,68,.2)' : notification.type === 'warning' ? 'rgba(234,179,8,.2)' : notification.type === 'success' ? 'rgba(34,197,94,.2)' : 'rgba(255,255,255,.1)',
          color: notification.type === 'error' ? '#f87171' : notification.type === 'warning' ? '#fbbf24' : notification.type === 'success' ? '#4ade80' : '#fff',
          border: `1px solid ${notification.type === 'error' ? 'rgba(239,68,68,.3)' : notification.type === 'warning' ? 'rgba(234,179,8,.3)' : notification.type === 'success' ? 'rgba(34,197,94,.3)' : 'rgba(255,255,255,.2)'}`,
          backdropFilter: 'blur(12px)',
        }}>{notification.msg}</div>
      )}

      {/* ── Layout: left controls panel + right output feed ── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative', zIndex: 1 }}>

        {/* Loading overlay */}
        {isLoadingFromUrl && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 100, gap: 16 }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,.08)', borderTopColor: '#FF7E67', borderRadius: '50%', animation: 'zen-spin 1s linear infinite' }} />
            <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 14 }}>Loading…</span>
          </div>
        )}

        {/* LEFT — control panel (desktop only; mobile gets the sheet below) */}
        {!isCompact && (
          <div style={{ flex: `0 0 ${panelWidth}px`, width: panelWidth, minWidth: 0, height: '100%', borderRight: '1px solid var(--border)' }}>
            {controlPanel}
          </div>
        )}

        {/* RIGHT — output feed. minHeight:0 so Gallery (which does not scroll)
            is bounded by the viewport instead of stretching the column. */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>

          {/* ── Results toolbar ─────────────────────────────────────── */}
          <div style={{ flex: '0 0 auto', padding: isCompact ? '0 14px' : '0 20px', borderBottom: '1px solid var(--border)' }}>
            {/* Wraps rather than scrolls — an overflowX row silently hid the tile-size
                and sort controls off the right edge at laptop widths. */}
            <div className="row" style={{ minHeight: 54, justifyContent: 'space-between', gap: 12, rowGap: 8, flexWrap: 'wrap', minWidth: 0, padding: '8px 0' }}>
              <div className="row" style={{ gap: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Results
                  {flatItems.length > 0 && <span style={{ color: 'var(--text-4)', fontWeight: 500 }}> · {flatItems.length}</span>}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  {[['all', 'All'], ['image', 'Models'], ['workflow', 'Workflows']].map(([v, l]) => (
                    <button key={v} onClick={() => { setActiveFilter(v); setModelFilter(null); }} className="chip" style={{
                      height: 26, cursor: 'pointer', whiteSpace: 'nowrap',
                      background: activeFilter === v ? 'rgba(255,77,141,.10)' : 'transparent',
                      color: activeFilter === v ? '#FFC2B5' : 'var(--text-3)',
                      borderColor: activeFilter === v ? 'rgba(255,77,141,.30)' : 'var(--border)',
                    }}>{l}</button>
                  ))}
                </div>
              </div>

              <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                {/* View mode — labelled, not a mystery icon */}
                <div className="row" style={{ gap: 3, padding: 3, borderRadius: 9, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)' }}>
                  {VIEW_MODES.map(({ id, label, Icon, hint }) => (
                    <button key={id} onClick={() => setViewMode(id)} title={hint}
                      style={{
                        height: 26, padding: isCompact ? '0 8px' : '0 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                        background: viewMode === id ? 'rgba(255,255,255,.12)' : 'transparent',
                        color: viewMode === id ? '#fff' : 'var(--text-3)',
                        fontSize: 12, fontWeight: 600,
                      }}>
                      <Icon s={13} />{!isCompact && label}
                    </button>
                  ))}
                </div>

                {/* Tile size — grid only */}
                {viewMode === 'grid' && (
                  <div className="row" style={{ gap: 3, padding: 3, borderRadius: 9, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)' }}>
                    {Object.keys(GRID_SIZES).map(k => (
                      <button key={k} onClick={() => setGridSize(k)} title={`${k === 'S' ? 'Small' : k === 'M' ? 'Medium' : 'Large'} tiles`}
                        style={{
                          width: 24, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                          background: gridSize === k ? 'rgba(255,255,255,.12)' : 'transparent',
                          color: gridSize === k ? '#fff' : 'var(--text-3)',
                        }}>{k}</button>
                    ))}
                  </div>
                )}

                {/* Sort */}
                <button
                  onClick={() => setSortOrder(o => (o === 'newest' ? 'oldest' : 'newest'))}
                  title="Toggle sort order"
                  className="chip"
                  style={{ height: 32, cursor: 'pointer', gap: 6, color: 'var(--text-2)', whiteSpace: 'nowrap' }}
                >
                  {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
                  <span style={{ display: 'inline-grid', placeItems: 'center', transform: sortOrder === 'newest' ? 'none' : 'rotate(180deg)' }}><CaretIcon s={10} /></span>
                </button>
              </div>
            </div>

            {/* Secondary: filter by a specific model/workflow */}
            {models.length > 0 && (
              <div className="row" style={{ gap: 8, paddingBottom: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0 }}>Filter by</span>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <select
                    value={modelFilter || ''}
                    onChange={e => setModelFilter(e.target.value || null)}
                    style={{
                      background: 'rgba(255,255,255,.06)',
                      border: `1px solid ${modelFilter ? 'rgba(255,77,141,.30)' : 'var(--border)'}`,
                      borderRadius: 8,
                      color: modelFilter ? '#FFC2B5' : 'var(--text-3)',
                      fontSize: 12,
                      padding: '4px 28px 4px 10px',
                      cursor: 'pointer',
                      minWidth: 160,
                      maxWidth: 260,
                      appearance: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="">All {activeFilter === 'workflow' ? 'Workflows' : activeFilter === 'image' ? 'Models' : 'Models & Workflows'}</option>
                    {models
                      .filter(m => activeFilter === 'all' || (activeFilter === 'image' && !m.isWorkflow) || (activeFilter === 'workflow' && m.isWorkflow))
                      .map(m => (
                        <option key={m.model_id} value={m.model_id}>
                          {m.model_name || m.name}
                        </option>
                      ))
                    }
                  </select>
                  <svg style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: modelFilter ? '#FFC2B5' : 'var(--text-3)' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                {modelFilter && (
                  <button onClick={() => setModelFilter(null)} className="btn btn-ghost btn-sm" style={{ gap: 5 }}>
                    <CloseIcon s={11} /> Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Scrollable output area ──────────────────────────────── */}
          <div
            ref={scrollContainerRef}
            className={isCompact ? 'no-scrollbar' : ''}
            style={{
              flex: 1, minHeight: 0,
              overflowY: viewMode === 'gallery' ? 'hidden' : 'auto',
              overflowX: 'hidden', overflowAnchor: 'none',
              padding: isCompact ? 14 : 20, display: 'flex', flexDirection: 'column', minWidth: 0,
            }}
          >
            {generations.length === 0 && !isGenerating ? (
              /* Empty state */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 40 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>Ready to create</div>
                  <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 420, lineHeight: 1.55 }}>
                    {isCompact
                      ? 'Tap Create below to write a prompt, then press Generate.'
                      : 'Write a prompt on the left and press Generate — or ⌘/Ctrl + Enter.'}
                  </div>
                </div>
                <div className="row" style={{ gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button className="btn btn-primary" onClick={() => setModelSelectorOpen(true)}>Browse models</button>
                  <button className="btn btn-ghost" onClick={() => loadOldGenerations(true)} disabled={oldGenerationsLoading}>
                    {oldGenerationsLoading ? 'Loading…' : '↑ Load recent generations'}
                  </button>
                </div>

                {/* Starters — fill the prompt in one click */}
                {getTextParams().length > 0 && (
                  <div style={{ maxWidth: 560, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-4)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 10 }}>Or start from</div>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {STARTER_PROMPTS.map(s => (
                        <button key={s.label} className="chip" title={s.text}
                          onClick={() => handleUseAsPrompt(s.text)}
                          style={{ height: 30, cursor: 'pointer', color: 'var(--text-2)' }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : viewMode === 'gallery' ? (
              <ZenGalleryView
                items={flatItems}
                activeIndex={galleryIndex}
                onActiveIndexChange={setGalleryIndex}
                onRerun={handleRerun}
                onViewDetails={(gen) => setDetailViewJob(gen)}
                onLoadOlder={() => loadOldGenerations(!generations.some(g => g.isOld))}
                loadingOlder={oldGenerationsLoading}
                hasMoreOlder={hasMoreOldGenerations || !generations.some(g => g.isOld)}
                keyboardEnabled={!detailViewJob && !compareState && !modelSelectorOpen && !settingsOpen}
                likedIds={likedIds}
                formatCost={formatCost}
                {...cardHandlers}
              />
            ) : (
              <>
                {viewMode === 'grid' ? (
                  /* Aligned grid — reads left→right, top→bottom. CSS `columns`
                     used to fill column 1 top-to-bottom, which is why the newest
                     items all landed in the first column. */
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_SIZES[gridSize]}px, 1fr))`,
                    gap: 12,
                    alignContent: 'start',
                    // Without this, grid stretch overrides each tile's 1:1
                    // aspect-ratio and the row height comes from its tallest card.
                    alignItems: 'start',
                  }}>
                    {flatItems.map(item => (
                      <GenerationCard
                        key={item.key}
                        generation={item.gen}
                        output={item.output}
                        badge={item.outputCount > 1 ? `${item.outputIndex + 1}/${item.outputCount}` : null}
                        fit="square"
                        isActive={item.gen.id === activeGenId}
                        onRerun={() => handleRerun(item.gen)}
                        onViewDetails={() => setDetailViewJob(item.gen)}
                        onDismiss={item.gen.status === 'failed' ? () => handleDismiss(item.gen) : undefined}
                        liked={likedIds.has(item.gen.jobId || item.gen.id)}
                        likePending={likePendingIds.has(item.gen.jobId || item.gen.id)}
                        {...cardHandlers}
                      />
                    ))}
                  </div>
                ) : (
                  /* List — one generation per row, prompt visible.
                     maxWidth 640 made every row a full screen tall — you saw one
                     result at a time. 480 keeps prompt + preview scannable. */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 480, margin: '0 auto' }}>
                    {ordered.map(gen => {
                      const p = getPrompt(gen);
                      return (
                        <div key={gen.id}>
                          {p && (
                            <div title={p} style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {p}
                            </div>
                          )}
                          <GenerationCard
                            generation={gen}
                            isActive={gen.id === activeGenId}
                            onRerun={() => handleRerun(gen)}
                            onViewDetails={() => setDetailViewJob(gen)}
                            onDismiss={gen.status === 'failed' ? () => handleDismiss(gen) : undefined}
                            liked={likedIds.has(gen.jobId || gen.id)}
                            likePending={likePendingIds.has(gen.jobId || gen.id)}
                            {...cardHandlers}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Load older */}
                {(hasMoreOldGenerations || !generations.some(g => g.isOld)) && (
                  <button onClick={() => loadOldGenerations(!generations.some(g => g.isOld))} disabled={oldGenerationsLoading}
                    style={{ alignSelf: 'center', marginTop: 20, padding: '9px 22px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, color: 'var(--text-3)', fontSize: 13, fontWeight: 500, cursor: oldGenerationsLoading ? 'wait' : 'pointer' }}
                  >
                    {oldGenerationsLoading ? 'Loading…' : generations.some(g => g.isOld) ? '↓ Load older' : '↓ Load previous generations'}
                  </button>
                )}
              </>
            )}
          </div>{/* end scroll-y */}

          {/* ── Mobile compose bar ───────────────────────────────────── */}
          {isCompact && (
            <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', background: 'rgba(10,10,12,.94)', backdropFilter: 'blur(12px)', padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                onClick={() => setControlsOpen(true)}
                style={{ flex: 1, minWidth: 0, height: 42, padding: '0 14px', textAlign: 'left', borderRadius: 12, background: 'rgba(255,255,255,.05)', border: '1px solid var(--border)', color: 'var(--text-3)', fontSize: 13, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {Object.values(textInputs).find(v => typeof v === 'string' && v.trim()) || 'Describe what you want…'}
              </button>
              <button className="btn btn-primary" style={{ height: 42, flexShrink: 0 }} onClick={() => setControlsOpen(true)}>
                Create
              </button>
            </div>
          )}
        </div>{/* end right output panel */}
      </div>{/* end left-right layout */}

      {/* ── Mobile control sheet ─────────────────────────────────────── */}
      {isCompact && controlsOpen && (
        <>
          <div onClick={() => setControlsOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 900, backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, height: '92dvh', zIndex: 901,
            background: 'var(--bg-2, #0a0a0c)', borderTop: '1px solid var(--border)',
            borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            <div className="row" style={{ justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Create</span>
              <button onClick={() => setControlsOpen(false)} aria-label="Close controls"
                style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,.06)', border: '1px solid var(--border)', color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                <CloseIcon s={14} />
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>{controlPanel}</div>
          </div>
        </>
      )}

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        setSettings={setSettings}
        selectedModel={selectedModel}
        textInputs={textInputs}
        setTextInputs={setTextInputs}
        uploadedImages={uploadedImages}
        setUploadedImages={setUploadedImages}
      />

      {/* Generation detail modal */}
      {detailViewJob && (
        <ZenGenerationModal
          generation={detailViewJob}
          generations={ordered}
          onClose={() => setDetailViewJob(null)}
          onReuse={(job) => { handleRerun(job); setDetailViewJob(null); }}
          onIterateWith={(url) => { handleUseAsInput(url); setDetailViewJob(null); }}
          onEditImage={(url) => { handleEditImage(url); setDetailViewJob(null); }}
          onUseAsInput={(url) => { handleUseAsInput(url); setDetailViewJob(null); }}
          formatCost={formatCost}
          imageParams={imageParams}
        />
      )}

      {/* Before/after viewer */}
      {compareState && (
        <ImageViewerModal
          isOpen
          images={compareState.images}
          inputImages={compareState.inputImages}
          imageParams={imageParams}
          initialIndex={compareState.initialIndex}
          initialCompare
          onClose={() => setCompareState(null)}
          onUseAsInput={(url) => { handleUseAsInput(url); setCompareState(null); }}
        />
      )}

      {/* Model selector modal */}
      {isMobile
        ? <ModelSelectorSheet isOpen={modelSelectorOpen} onClose={() => setModelSelectorOpen(false)} selectedModel={selectedModel} onSelectModel={(m) => { handleSelectModel(m); setModelSelectorOpen(false); }} formatCost={formatCost} />
        : <ModelSelectorModal isOpen={modelSelectorOpen} onClose={() => setModelSelectorOpen(false)} selectedModel={selectedModel} onSelectModel={(m) => { handleSelectModel(m); setModelSelectorOpen(false); }} formatCost={formatCost} />
      }
      </div>{/* main column with orbs+topbar */}
    </div>{/* screen-root */}
    </AppScreenWrapper>
  );
}
